/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { Platform } from 'react-native';
import {
  SSO_SIGNUP_FEATURE_FLAGS,
  SSO_SIGNUP_FEATURE_FLAGS_DEFAULT,
  SSO_SOCIAL_FEATURE_FLAGS,
  type SsoSignupFeatureFlags,
} from '../constants/featureFlags';
import {
  resolveSocialProviderOrder,
  toSocialProviderVisibility,
  type SocialProviderVisibility,
} from '../utils/socialProviders';
import { useFlagEnabled, useSetting } from './settings';

type UseSignupFeatureFlagsResult = SsoSignupFeatureFlags & {
  isSettingsLoading: boolean;
};

const isSignupFeatureFlagsObject = (
  value: unknown,
): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

// JSON blob only — social visibility and 2FA come from v6 flags below.
const normalizeSignupFeatureFlags = (
  flags: Record<string, unknown>,
): Partial<SsoSignupFeatureFlags> => {
  const normalized: Partial<SsoSignupFeatureFlags> = {};

  const parentalGuidance = flags.parental_guidance;
  const legacyParentalGuidance = flags.parental_guidence;

  if (typeof parentalGuidance === 'boolean') {
    normalized.parental_guidance = parentalGuidance;
  } else if (typeof legacyParentalGuidance === 'boolean') {
    normalized.parental_guidance = legacyParentalGuidance;
  }

  if (flags.provider_order !== undefined) {
    normalized.provider_order = resolveSocialProviderOrder(
      flags.provider_order,
    );
  }

  return normalized;
};

const parseSignupFeatureFlags = (
  value: unknown,
): Partial<SsoSignupFeatureFlags> | undefined => {
  if (isSignupFeatureFlagsObject(value)) {
    return normalizeSignupFeatureFlags(value);
  }

  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }

  // Tolerate trailing commas from remote config editors.
  const parsedFlags = JSON.parse(value.replace(/,(\s*[}\]])/g, '$1'));
  return isSignupFeatureFlagsObject(parsedFlags)
    ? normalizeSignupFeatureFlags(parsedFlags)
    : undefined;
};

export const useSignupFeatureFlags = (): UseSignupFeatureFlagsResult => {
  const { value, isSettingsLoading: isJsonLoading } = useSetting(
    SSO_SIGNUP_FEATURE_FLAGS,
  );
  const google = useFlagEnabled(SSO_SOCIAL_FEATURE_FLAGS.GOOGLE, true);
  const apple = useFlagEnabled(SSO_SOCIAL_FEATURE_FLAGS.APPLE, true);
  const facebook = useFlagEnabled(SSO_SOCIAL_FEATURE_FLAGS.FACEBOOK, false);
  const wechat = useFlagEnabled(SSO_SOCIAL_FEATURE_FLAGS.WECHAT, false);
  const login2fa = useFlagEnabled(SSO_SOCIAL_FEATURE_FLAGS.LOGIN_2FA, true);

  const isSettingsLoading =
    isJsonLoading ||
    google.isSettingsLoading ||
    apple.isSettingsLoading ||
    facebook.isSettingsLoading ||
    wechat.isSettingsLoading ||
    login2fa.isSettingsLoading;

  return useMemo(() => {
    try {
      return {
        ...SSO_SIGNUP_FEATURE_FLAGS_DEFAULT,
        ...parseSignupFeatureFlags(value),
        google_login: google.enabled,
        apple_login: apple.enabled,
        facebook_login: facebook.enabled,
        wechat_login: wechat.enabled,
        enable_sso_login_2fa_v6: login2fa.enabled,
        isSettingsLoading,
      };
    } catch {
      return {
        ...SSO_SIGNUP_FEATURE_FLAGS_DEFAULT,
        google_login: google.enabled,
        apple_login: apple.enabled,
        facebook_login: facebook.enabled,
        wechat_login: wechat.enabled,
        enable_sso_login_2fa_v6: login2fa.enabled,
        isSettingsLoading,
      };
    }
  }, [
    apple.enabled,
    facebook.enabled,
    google.enabled,
    isSettingsLoading,
    login2fa.enabled,
    value,
    wechat.enabled,
  ]);
};

type UseSocialProviderVisibilityResult = {
  providerOrder: SsoSignupFeatureFlags['provider_order'];
  visibility: SocialProviderVisibility;
};

export const useSocialProviderVisibility =
  (): UseSocialProviderVisibilityResult => {
    const flags = useSignupFeatureFlags();

    return useMemo(() => {
      const visibility = toSocialProviderVisibility(flags);

      // Apple native sign-in is iOS-only; web uses SSO popup.
      if (Platform.OS === 'android') {
        visibility.apple = false;
      }

      return {
        providerOrder: flags.provider_order,
        visibility,
      };
    }, [flags]);
  };
